'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useSyncExternalStore } from 'react';
import {
  getServerSnapshot,
  getSnapshot,
  logout,
  subscribe,
} from '@/lib/auth';

const NAV: { href: string; label: string }[] = [
  { href: '/', label: 'Dashboard' },
  { href: '/transactions', label: 'Transactions' },
  { href: '/holdings', label: 'Holdings' },
  { href: '/categories', label: 'Categories' },
  { href: '/trajectory', label: 'Goals' },
  { href: '/rules', label: 'Rules' },
  { href: '/imports', label: 'Imports' },
  { href: '/settings', label: 'Settings' },
];

export function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const auth = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Hide the nav on auth pages — they're full-bleed and the chrome would
  // imply the user is logged in.
  const onAuthPage =
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/verify-email') ||
    pathname.startsWith('/reset-password') ||
    pathname.startsWith('/setup-2fa');
  if (onAuthPage) return null;

  async function onLogout() {
    await logout();
    router.replace('/login');
  }

  return (
    <aside className="flex w-56 shrink-0 flex-col gap-6 px-4 py-6">
      <Link
        href="/"
        className="text-3xl leading-none tracking-tight lowercase"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        flowlet
      </Link>
      <nav className="flex flex-col gap-1 text-sm">
        {NAV.map((item) => {
          const active =
            item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={
                'pressable rounded-full px-3 py-2 transition-colors ' +
                (active
                  ? 'bg-raised font-medium text-fg-primary shadow-sm'
                  : 'text-fg-muted hover:bg-raised hover:text-fg-primary')
              }
            >
              {item.label}
            </Link>
          );
        })}
      </nav>
      <div className="mt-auto flex flex-col gap-2 text-xs">
        {auth.status === 'authenticated' && auth.me && (
          <>
            <div className="truncate px-3 text-fg-muted" title={auth.me.email}>
              {auth.me.email}
            </div>
            {!auth.me.totp_enabled && (
              <Link
                href="/setup-2fa"
                className="rounded-full px-3 py-2 font-medium hover:bg-raised"
                style={{ color: 'var(--hero-start)' }}
              >
                Set up 2FA →
              </Link>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="rounded-full px-3 py-2 text-left text-fg-muted hover:bg-raised hover:text-negative"
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
